<script>
import { mapActions, mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import CloseIcon from '@mobile/components/Icons/CloseIcon.vue';
import InputError from '@mobile/components/Form/InputError.vue';
import Loader from '@mobile/components/Loader.vue';
import InputControl from '@mobile/components/Form/InputControl.vue';

import BaseView from '@mobile/components/BaseView.vue';
import SaveIcon from '@mobile/components/Icons/SaveIcon.vue';
import TitleBar from '@/components/TitleBar.vue';
import AutosizeTextarea from '@/components/Form/AutosizeTextarea.vue';
import CheckboxGroup from '@/components/CheckboxGroup.vue';
import InputControlDate from '@/components/Form/InputControlDate.vue';
import { ISSUE_PRIORITY, ISSUE_PERMISSIONS } from '@/constants';
import DropdownModal from '@/views/ConductInspection/Modals/DropdownModal.vue';
import AddPhotos from '@/components/AddPhotos.vue';

export default {
    name: 'RaiseIssue',

    components: {
        AddPhotos,
        CloseIcon,
        InputError,
        DropdownModal,
        Loader,
        InputControlDate,
        InputControl,
        AutosizeTextarea,
        CheckboxGroup,
        BaseView,
        TitleBar,
        SaveIcon,
    },

    data: () => ({
        showAssigneeModal: false,
        showPriorityModal: false,
        redirectToEdit: false,
        form: {
            title: '',
            description: '',
            site: null,
            assignees: [],
            priority: ISSUE_PRIORITY.MEDIUM,
            deadline: null,
            suggestedCorrectiveAction: '',
            photos: [],
        },
    }),

    validations: {
        form: {
            title: { required },
            priority: { required },
        },
    },

    computed: {
        ...mapGetters({
            filterOptions: 'issues/filterOptions',
            isLoading: 'issues/isLoading',
            hasPermission: 'authentication/hasPermission',
        }),

        priorities() {
            return {
                [ISSUE_PRIORITY.MEDIUM]: this.$t('Normal'),
                [ISSUE_PRIORITY.HIGH]: this.$t('High'),
            };
        },

        priorityOptions() {
            return [
                {
                    key: ISSUE_PRIORITY.MEDIUM,
                    value: this.$t('Normal'),
                    status: 'bg-yellow',
                },
                {
                    key: ISSUE_PRIORITY.HIGH,
                    value: this.$t('High'),
                    status: 'bg-red',
                },
            ];
        },

        assignees() {
            const encodeAssignees = (assignees) => Object.keys(assignees).map((key) => ({
                uuid: key,
                title: assignees[key],
            }));

            return encodeAssignees(this.filterOptions.assignees);
        },

        selectedAssignees() {
            return this.form.assignees.map(String);
        },

        canCreateIssueWithAssignee() {
            return this.hasPermission(
                ISSUE_PERMISSIONS.CREATE_ISSUE_WITH_ASSIGNEE,
            );
        },

        hasMoreThanOneSite() {
            return (
                this.filterOptions.sites
                && Object.keys(this.filterOptions.sites).length > 1
            );
        },
    },

    created() {
        this.raiseIssuesScreenCreated();
    },

    methods: {
        ...mapActions({
            raiseIssuesScreenCreated: 'issues/raiseIssuesScreenCreated',
            issueRaised: 'issues/issueRaised',
        }),

        async formSubmitted() {
            if (!this.hasMoreThanOneSite && this.filterOptions.sites) {
                const [firstSite] = Object.keys(this.filterOptions.sites);
                this.form.site = Number(firstSite);
            }

            this.$v.$touch();

            if (this.$v.$invalid) {
                this.$toasted.show(
                    this.$t('This page has form errors, please fix them'),
                    { type: 'error' },
                );
                this.redirectToEdit = false;
            } else {
                try {
                    const { id } = await this.issueRaised(this.form);
                    if (this.redirectToEdit) {
                        this.$router.push({
                            name: 'issues-edit',
                            params: { id },
                        });
                    } else {
                        this.$router.go(-1);
                    }
                    this.$gtm.dataLayer().push({
                        event: 'issueCreated',
                    });
                    this.$toasted.show(this.$t('Raised successfully'), {
                        type: 'success',
                    });
                } catch (error) {
                    this.$toasted.show(
                        this.$t(
                            "We couldn't save your issue, please try again later",
                        ),
                        { type: 'error' },
                    );
                }

                this.redirectToEdit = false;
            }
        },

        selectAssigneeClicked() {
            this.showAssigneeModal = true;
        },

        assigneeModalSaved(assignees) {
            this.form.assignees = assignees.map(Number);
            this.assigneeModalClosed();
        },

        assigneeModalClosed() {
            this.showAssigneeModal = false;
        },

        selectPriorityClicked() {
            this.showPriorityModal = true;
        },

        priorityModalSelected(priority) {
            this.form.priority = priority;
            this.priorityModalClosed();
        },

        priorityModalClosed() {
            this.showPriorityModal = false;
        },
    },
};
</script>

<template>
    <BaseView contentClass="bg-white">
        <template v-slot:top>
            <TitleBar
                :title="$t('Raise Issue')"
                @left-click="$router.go(-1)"
                @right-click="formSubmitted"
            >
                <template v-slot:leftIcon>
                    <CloseIcon />
                </template>

                <template v-slot:rightIcon>
                    <SaveIcon />
                </template>
            </TitleBar>
        </template>

        <form class="raise-issue" @submit.prevent="formSubmitted" novalidate>
            <InputControl :label="$t('Title')">
                <input
                    class="form-input form-control form-input-bordered w-full"
                    id="title"
                    v-model.trim.lazy="$v.form.title.$model"
                    name="title"
                    type="text"
                    :placeholder="$t('Enter title')"
                />
                <div v-if="$v.form.title.$anyError">
                    <InputError
                        v-if="$v.form.title.$dirty"
                        :condition="!$v.form.title.required"
                    >
                        {{
                            $t("The {field} field is required", {
                                field: $t("title")
                            })
                        }}
                    </InputError>
                </div>
            </InputControl>

            <InputControl :label="$t('Description')">
                <AutosizeTextarea
                    v-model.trim.lazy="form.description"
                    :placeholder="$t('Enter description (optional)')"
                />
            </InputControl>

            <InputControl>
                <AddPhotos v-model="form.photos" />
            </InputControl>

            <InputControl v-show="hasMoreThanOneSite" :label="$t('Site')">
                <select
                    v-model="form.site"
                    class="form-select form-input form-control form-input-bordered w-full"
                    :class="{
                        empty: !form.site
                    }"
                >
                    <option :value="null">{{
                        $t("Select site (optional)")
                    }}</option>
                    <option
                        v-for="(name, id) in filterOptions.sites"
                        :key="id"
                        :value="parseInt(id)"
                        >{{ name }}</option
                    >
                </select>
            </InputControl>

            <InputControl
                v-show="canCreateIssueWithAssignee"
                :label="$t('Assignee')"
            >
                <div
                    class="form-select form-input form-control form-input-bordered w-full"
                    :class="{
                        empty:
                            form.assignees === null ||
                            form.assignees.length === 0
                    }"
                    @click="selectAssigneeClicked"
                >
                    <span
                        v-if="
                            form.assignees === null ||
                                form.assignees.length === 0
                        "
                        >{{ $t("Select assignee (optional)") }}</span
                    >
                    <span v-else>{{
                        form.assignees
                            .map(id => this.filterOptions.assignees[id])
                            .join(", ")
                    }}</span>
                </div>
            </InputControl>

            <InputControl :label="$t('Priority')">
                <CheckboxGroup
                    v-model="form.priority"
                    :options="priorityOptions"
                />
            </InputControl>

            <InputControl
                v-if="canCreateIssueWithAssignee"
                :label="$t('Due at')"
            >
                <InputControlDate
                    v-model="form.deadline"
                    :class="{
                        empty: form.deadline === null
                    }"
                    :placeholder="$t('Select deadline (optional)')"
                    display-time
                    wrap-size
                />
            </InputControl>

            <InputControl :label="$t('Suggested corrective action')">
                <AutosizeTextarea
                    v-model.trim.lazy="form.suggestedCorrectiveAction"
                    :placeholder="
                        $t('Enter sugested corrective action (optional)')
                    "
                />
            </InputControl>

            <button
                class="btn btn-default btn-primary main-button"
                type="submit"
            >
                <span v-show="!isLoading">{{ $t("Save") }}</span>
                <Loader v-show="isLoading" width="20" />
            </button>

            <button
                class="btn btn-default text-primary mt-4 w-full medium:w-auto medium:self-center"
                type="submit"
                @click="redirectToEdit = true"
            >
                <span v-show="!isLoading">{{ $t("Save & edit issue") }}</span>
                <Loader v-show="isLoading" width="20" />
            </button>
        </form>

        <portal to="modals">
            <transition name="fade">
                <DropdownModal
                    v-if="showAssigneeModal"
                    :responses="assignees"
                    :response="selectedAssignees"
                    :multiple="true"
                    @save="assigneeModalSaved"
                    @close="assigneeModalClosed"
                />
            </transition>
        </portal>
    </BaseView>
</template>
